import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { EventsActions } from 'store/actions';
import { EventsSelectors } from 'store/selectors';
import { RootState } from 'store/rootReducer';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { getCalendarDateFilter } from 'components/filters/dateFilters';

export const useCalendarEvents = (events: any[], currentDate: Date) =>
  useMemo(() => {
    if (!Array.isArray(events) || events.length === 0) {
      return [];
    }

    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    return events.flatMap((event: any) => {
      if (!event.sessions || event.sessions.length <= 1) {
        return [
          {
            id: event.id,
            eventId: event.id,
            title: event.title,
            start: event.start_at,
            end: event.end_at,
            sessions: event.sessions,
            timezone: event.timezone,
            isDraft: event.is_draft,
          },
        ];
      }

      const seenDays = new Set<string>();

      return event.sessions
        .filter((session: any) => {
          const date = new Date(session.start_at);
          return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
        })
        .filter((session: any) => {
          const date = new Date(session.start_at);
          const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

          if (seenDays.has(dayKey)) return false;

          seenDays.add(dayKey);
          return true;
        })
        .map((session: any, index: number) => ({
          id: `${event.id}-${index}`,
          eventId: event.id,
          title: event.title,
          start: session.start_at,
          end: session.end_at,
          sessions: event.sessions,
          timezone: event.timezone,
          isDraft: event.is_draft,
        }));
    });
  }, [events, currentDate]);

export const useEventsByDate = (calendarEvents: any[]): Map<string, any[]> =>
  useMemo(() => {
    const map = new Map<string, any[]>();
    calendarEvents.forEach((event) => {
      const dayKey = format(new Date(event.start), 'yyyy-MM-dd');
      if (!map.has(dayKey)) map.set(dayKey, []);
      (map.get(dayKey) as any[]).push(event);
    });
    return map;
  }, [calendarEvents]);

export const useEventPopover = (events: any[], dispatch: any) => {
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [anchorPosition, setAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const eventDetailState = useSelector((state: RootState) =>
    selectedEvent ? EventsSelectors.eventStateById(state, selectedEvent.id) : null,
  );

  useEffect(() => {
    if (eventDetailState?.isFetched && eventDetailState?.entity) {
      setSelectedEvent(eventDetailState.entity);
    }
  }, [eventDetailState?.isFetched, eventDetailState?.entity]);

  const handleEventClick = (info: any) => {
    const eventId = Number(info.event.extendedProps.eventId);
    if (!info.event.extendedProps.isDraft) dispatch(EventsActions.getEvent.request({ id: eventId }));
    setSelectedEvent(events.find((e) => e.id === eventId));

    // Popup dimensions
    const popupWidth = 256;
    const popupHeight = 418;
    const padding = 10;

    // Get viewport dimensions and scroll position
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    // Get click coordinates
    const clickX = info.jsEvent.clientX; // viewport coordinates
    const clickY = info.jsEvent.clientY; // viewport coordinates
    const { pageX } = info.jsEvent; // document coordinates
    const { pageY } = info.jsEvent; // document coordinates

    // Find the calendar container to get its position
    const calendarElement = info.el.closest('.fc');
    const calendarRect = calendarElement ? calendarElement.getBoundingClientRect() : null;

    // Calculate available space relative to calendar position
    let availableSpaceAbove;
    let availableSpaceBelow;

    if (calendarRect) {
      // Space available above the click, considering calendar boundaries
      availableSpaceAbove = Math.max(0, clickY - calendarRect.top);
      // Space available below the click, considering calendar boundaries and viewport
      availableSpaceBelow = Math.min(
        viewportHeight - clickY, // space to bottom of viewport
        calendarRect.bottom - clickY, // space to the bottom of the calendar
      );
    } else {
      // Fallback to viewport-based calculation
      availableSpaceAbove = clickY;
      availableSpaceBelow = viewportHeight - clickY;
    }

    // Calculate horizontal position
    let left = pageX + 10; // 10px to the right
    if (clickX + popupWidth + padding > viewportWidth) {
      left = pageX - popupWidth - 10; // Show to the left
      if (left < scrollLeft + padding) {
        left = scrollLeft + padding;
      }
    }

    // Calculate vertical position based on available space
    let top: number;

    if (availableSpaceBelow >= popupHeight + padding) {
      // Enough space below - show below click
      top = pageY + 10;
    } else if (availableSpaceAbove >= popupHeight + padding) {
      // Not enough space below, but enough above - positions above click
      top = pageY - popupHeight - 10;
    } else if (availableSpaceAbove > availableSpaceBelow) {
      // More space above - position so popup uses most of the available space above
      const maxHeightAbove = Math.min(popupHeight, availableSpaceAbove - padding);
      top = pageY - maxHeightAbove - 10;
    } else {
      // More space below - position near click
      top = pageY - 10;
    }

    // Final boundary check to ensure the popup is visible in the viewport
    const minTop = scrollTop + padding;
    const maxTop = scrollTop + viewportHeight - popupHeight - padding;
    const finalTop = Math.max(minTop, Math.min(top, maxTop));

    setAnchorPosition({ top: finalTop, left });
  };

  const handleClosePopover = () => {
    setAnchorPosition(null);
    setSelectedEvent(null);
  };

  return {
    selectedEvent,
    anchorPosition,
    handleEventClick,
    handleClosePopover,
  };
};

type UseCalendarHandlersProps = {
  calendarRef: React.RefObject<any>;
  setCurrentDate: (date: Date) => void;
  queryParams: any;
  setQueryParams: (params: any) => void;
  events: any[]; // original API events
  calendarEvents: any[]; // events used by FullCalendar
  setMoreEvents: (events: any[]) => void;
  setMoreDate: (date: Date | null) => void;
  setIsMoreOpen: (value: boolean) => void;
  handleClosePopover: () => void;
};

const getEventsForDay = (date: Date, calendarEvents: any[], sourceEvents: any[]) => {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);

  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  const startMs = dayStart.getTime();
  const endMs = dayEnd.getTime();

  const calendarEventsForDay = calendarEvents.filter((event) => {
    const start = new Date(event.start).getTime();
    const end = event.end ? new Date(event.end).getTime() : start;

    return start < endMs && end > startMs;
  });

  const eventIds = [...new Set(calendarEventsForDay.map((e) => e.eventId))];

  return eventIds
    .map((id) => sourceEvents.find((e) => e.id === id))
    .filter(Boolean)
    .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
};

export const useCalendarHandlers = ({
  calendarRef,
  setCurrentDate,
  queryParams,
  setQueryParams,
  events,
  calendarEvents,
  setMoreEvents,
  setMoreDate,
  setIsMoreOpen,
  handleClosePopover,
}: UseCalendarHandlersProps) => {
  const queryParamsRef = useRef(queryParams);

  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  const updateMonth = (direction: 'prev' | 'next') => {
    handleClosePopover();

    const api = calendarRef.current?.getApi();
    if (!api) return;

    if (direction === 'prev') api.prev();
    if (direction === 'next') api.next();

    const date = api.getDate();
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), 1);

    setCurrentDate(normalizedDate);

    const newFilter = getCalendarDateFilter(startOfMonth(normalizedDate), endOfMonth(normalizedDate));

    setQueryParams({
      ...queryParamsRef.current,
      ...newFilter,
    });
  };

  const openEventsForDay = (date: Date) => {
    const eventsForDay = getEventsForDay(date, calendarEvents, events);

    if (!eventsForDay.length) return;

    setMoreEvents(eventsForDay);
    setMoreDate(date);
    setIsMoreOpen(true);
  };

  const handleDateClick = (arg: { date: Date }) => {
    openEventsForDay(arg.date);
  };

  const handleMoreLinkClick = (arg: any) => {
    const ids = [...new Set(arg.allSegs.map((seg: any) => seg.event.extendedProps.eventId))];

    const eventsForDay = ids.map((id) => events.find((e) => e.id === id)).filter(Boolean);

    setMoreEvents(eventsForDay);
    setMoreDate(arg.date);
    setIsMoreOpen(true);

    return 'none';
  };

  const handlePrevMonth = () => updateMonth('prev');
  const handleNextMonth = () => updateMonth('next');

  return {
    handlePrevMonth,
    handleNextMonth,
    handleDateClick,
    handleMoreLinkClick,
  };
};
